import CloseIcon from '@mui/icons-material/Close';
import MailIcon from '@mui/icons-material/MailOutline';
import { Box, Dialog, IconButton, Stack, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import moment from 'moment';
import { decode } from 'quoted-printable';
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { scrollbarStyle } from '../../../../../shared/dashboard';
import { Dashboard, EmailBody } from '../../../../../types/files';
import { FolderChip } from '../../../../atoms/folder-label/FolderChip';

const useStyles = makeStyles(() => ({
    dialog: {
        '& .MuiDialog-paper': {
            width: '95%',
            height: '90vh',
            maxWidth: '1400px',
            borderRadius: '16px',
            margin: '20px',
        },
    },
    modalHeader: {
        padding: '20px 24px',
        borderBottom: '1px solid #e5e7eb',
        backgroundColor: '#f9fafb',
    },
    modalTitle: {
        fontSize: '18px',
        fontWeight: 600,
        color: '#1a1a1a',
    },
    closeButton: {
        width: '36px',
        height: '36px',
        borderRadius: '8px',
        backgroundColor: '#f3f4f6',
        color: '#6b7280',
        '&:hover': {
            backgroundColor: '#e5e7eb',
            color: '#1a1a1a',
        },
    },
    modalBody: {
        display: 'flex',
        flex: 1,
        overflow: 'hidden',
        height: 'calc(90vh - 81px)',
    },
    timelineSidebar: {
        width: '400px',
        borderRight: '1px solid #e5e7eb',
        backgroundColor: '#f9fafb',
        overflowY: 'auto',
        flexShrink: 0,
    },
    timelineHeader: {
        padding: '20px 20px 16px 20px',
        borderBottom: '2px solid #e5e7eb',
        backgroundColor: 'white',
    },
    timelineHeaderTitle: {
        fontSize: '11px',
        fontWeight: 600,
        color: '#9ca3af',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        marginBottom: '8px',
    },
    timelineCount: {
        fontSize: '24px',
        color: '#1a1a1a',
        fontWeight: 700,
    },
    timelineList: {
        padding: '16px',
    },
    dateGroup: {
        marginBottom: '24px',
        position: 'relative',
        '&::before': {
            content: '""',
            position: 'absolute',
            left: '9px',
            top: '8px',
            bottom: '-24px',
            width: '2px',
            backgroundColor: '#d1d5db',
        },
        '&:last-child': {
            marginBottom: 0,
            '&::before': {
                display: 'none',
            },
        },
    },
    dateHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '6px 0 6px 32px',
        marginBottom: '12px',
        borderBottom: '1px solid #e5e7eb',
        position: 'relative',
        '&::before': {
            content: '""',
            position: 'absolute',
            left: '4px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            backgroundColor: '#14b8a6',
            boxShadow: '0 0 0 3px rgba(20, 184, 166, 0.1)',
        },
    },
    timelineItem: {
        display: 'flex',
        gap: '12px',
        padding: 0,
        marginBottom: '12px',
        marginLeft: '32px',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    timelineContent: {
        flex: 1,
        minWidth: 0,
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '12px',
        border: '2px solid transparent',
        transition: 'all 0.2s',
        '&:hover': {
            backgroundColor: '#f3f4f6',
        },
    },
    timelineContentSelected: {
        borderColor: '#14b8a6',
        backgroundColor: '#f0fdfa',
    },
    emailSubject: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '8px',
        marginBottom: '8px',
    },
    emailIcon: {
        color: '#9ca3af !important',
        fontSize: '20px',
        flexShrink: 0,
    },
    emailSubjectText: {
        fontSize: '14px',
        fontWeight: 600,
        color: '#1a1a1a',
        lineHeight: 1.4,
        wordBreak: 'break-word',
    },
    emailFrom: {
        fontSize: '14px',
        color: '#6b7280',
        marginBottom: '4px',
    },
    emailPreview: {
        fontSize: '14px',
        color: '#9ca3af',
        lineHeight: 1.4,
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
    },
    emailHeaderInfo: {
        borderBottom: '1px solid #e5e7eb',
        paddingBottom: '20px',
        marginBottom: '24px',
    },
    emailSubjectLarge: {
        fontSize: '20px',
        fontWeight: 600,
        color: '#1a1a1a',
        marginBottom: '16px',
    },
    emailMeta: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    emailMetaRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '14px',
    },
    emailMetaLabel: {
        fontWeight: 600,
        color: '#6b7280',
        minWidth: '60px',
    },
    emailMetaValue: {
        color: '#1a1a1a',
    },
    emailBlock: {
        width: '100%',
        height: 'calc(100% - 140px)',
        overflowY: 'auto',
        ...scrollbarStyle,
    },
    bodyText: {
        fontSize: '14px',
        lineHeight: 1.6,
        color: '#1a1a1a',
        textAlign: 'left',
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
    },
    emailPlaceholder: {
        textAlign: 'center',
        color: '#9ca3af',
        padding: '60px 20px',
    },
    emailPlaceholderIcon: {
        color: '#9ca3af !important',
        marginTop: '2px',
        fontSize: '20px',
        flexShrink: 0,
    },
    emailPlaceholderTitle: {
        fontSize: '16px',
        fontWeight: 500,
        color: '#6b7280',
        marginBottom: '8px',
    },
    emailPlaceholderText: {
        fontSize: '14px',
        color: '#9ca3af',
    },
}));

const useTimelineStyles = makeStyles({
    sidebar: {
        width: '360px',
        borderRight: '1px solid #e5e7eb',
        backgroundColor: '#f9fafb',
        overflowY: 'auto',
        flexShrink: 0,
    },
    header: {
        padding: '20px 20px 16px 20px',
        borderBottom: '2px solid #e5e7eb',
        backgroundColor: 'white',
    },
    headerTitle: {
        fontSize: '11px',
        fontWeight: 600,
        color: '#9ca3af',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        marginBottom: '8px',
    },
    count: {
        fontSize: '24px',
        color: '#1a1a1a',
        fontWeight: 700,
    },
    list: {
        padding: '16px',
    },
    icon: {
        height: 18,
        width: 18,
    },
    dateGroup: {
        marginBottom: '24px',
        position: 'relative',
        '&::before': {
            content: '""',
            position: 'absolute',
            left: '9px',
            top: '8px',
            bottom: '-24px',
            width: '2px',
            backgroundColor: '#d1d5db',
        },
        '&:last-child::before': {
            display: 'none',
        },
    },
    dateHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '6px 0 6px 32px',
        marginBottom: '12px',
        borderBottom: '1px solid #e5e7eb',
        position: 'relative',
        '&::before': {
            content: '""',
            position: 'absolute',
            left: '4px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '12px',
            height: '12px',
            borderRadius: '50%',
            backgroundColor: '#14b8a6',
            boxShadow: '0 0 0 3px rgba(20, 184, 166, 0.1)',
        },
    },
    dateText: {
        fontFamily: 'Inter',
        fontSize: '12px',
        fontWeight: 600,
        color: '#6b7280',
        textTransform: 'uppercase',
    },
    dateRelative: {
        fontFamily: 'Inter',
        fontSize: '12px',
        color: '#9ca3af',
        fontWeight: 400,
        marginLeft: '8px',
    },
    dateBadge: {
        backgroundColor: '#14b8a6',
        color: 'white',
        padding: '4px 12px',
        borderRadius: '6px',
        fontSize: '12px',
        fontWeight: 600,
    },
    timelineItem: {
        display: 'flex',
        gap: '12px',
        padding: 0,
        marginBottom: '12px',
        marginLeft: '32px',
        cursor: 'pointer',
        transition: 'all 0.2s',
        border: '1px solid gray',
    },
    timelineContent: {
        flex: 1,
        minWidth: 0,
        backgroundColor: 'white',
        borderRadius: '8px',
        padding: '12px',
        border: '2px solid transparent',
        transition: 'all 0.2s',
        '&:hover': {
            backgroundColor: '#f3f4f6',
        },
    },
    timelineContentSelected: {
        borderColor: '#14b8a6',
        backgroundColor: '#f0fdfa',
    },
    fileName: {
        fontSize: '14px',
        fontWeight: 500,
        color: '#1a1a1a',
        lineHeight: 1.4,
        wordBreak: 'break-word',
    },
    fileIcon: {
        fontSize: '16px',
        flexShrink: 0,
    },
    fileSource: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '2px 8px',
        backgroundColor: '#eff6ff',
        border: '1px solid #bfdbfe',
        borderRadius: '4px',
        fontSize: '11px',
        color: '#1e40af',
        fontWeight: 500,
        marginTop: '4px',
        width: 'fit-content',
    },
    fileSourceLink: {
        color: '#1e40af',
        textDecoration: 'none',
        fontWeight: 500,
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    externalLinkBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '6px 12px',
        backgroundColor: '#eff6ff',
        border: '1px solid #bfdbfe',
        borderRadius: '6px',
        fontSize: '12px',
        color: '#1e40af',
        fontWeight: 500,
        marginTop: '6px',
        textDecoration: 'none',
        transition: 'all 0.2s',
        maxWidth: '100%',
        cursor: 'pointer',
        '&:hover': {
            backgroundColor: '#dbeafe',
            borderColor: '#93c5fd',
        },
    },
});



const useViewerStyles = makeStyles({
    panel: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#f9fafb',
    },
    header: {
        padding: '16px 24px',
        backgroundColor: 'white',
        borderBottom: '1px solid #e5e7eb',
    },
    title: {
        fontSize: '15px',
        fontWeight: 600,
        color: '#1a1a1a',
    },
    actionButton: {
        padding: '8px 16px',
        backgroundColor: '#14b8a6',
        color: 'white',
        borderRadius: '8px',
        fontSize: '13px',
        fontWeight: 500,
        textTransform: 'none',
        '&:hover': {
            backgroundColor: '#0d9488',
        },
    },
    container: {
        width: '100%',
        height: '100%',
        flex: 1,
        padding: '24px',
        overflow: 'hidden',
    },
    frame: {
        width: '100%',
        height: '100%',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        padding: '32px',
        maxWidth: '800px',
        margin: '0 auto',
    },
    placeholder: {
        textAlign: 'center',
        color: '#9ca3af',
    },
    placeholderIcon: {
        fontSize: '48px',
        marginBottom: '16px',
    },
    placeholderTitle: {
        fontSize: '16px',
        fontWeight: 500,
        color: '#6b7280',
        marginBottom: '8px',
    },
    placeholderText: {
        fontSize: '14px',
        color: '#9ca3af',
    },
    iframe: {
        border: 'none',
        borderRadius: '8px',
        width: '100%',
        height: '100%',
    },
    externalLinkButton: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 20px',
        backgroundColor: '#14b8a6',
        color: 'white',
        textDecoration: 'none',
        borderRadius: '8px',
        fontWeight: 500,
        fontSize: '14px',
        transition: 'all 0.2s',
        '&:hover': {
            backgroundColor: '#0d9488',
            textDecoration: 'none !important',
        },
    },
    emailIcon: {
        color: '#9ca3af !important',
        marginTop: '2px',
        fontSize: '24px',
        flexShrink: 0,
    },
});

// Atoms
const EmailViewer: React.FC<{ email: EmailBody | null; }> = memo(({ email }) => {
    const classes = useStyles();

    const bodyText = useMemo(() => decode(email?.content || 'No content.').toString()
        .replace(/\r\n/g, '\n').split('\n')
        .map(line => {
            const match = line.match(/^([>]+)\s?(.*)$/);
            if (!match) return line;

            const depth = match[1]?.length;
            const content = match[2]?.trim();
            if (!content) return '';
            return ' '.repeat(depth) + content;
        }).join('\n')
        , [email?.content]);

    if (!email) {
        return (
            <Stack className={classes.emailPlaceholder}>
                <MailIcon className={classes.emailPlaceholderIcon} />
                <Typography className={classes.emailPlaceholderTitle}>
                    {'No email selected'}
                </Typography>
                <Typography className={classes.emailPlaceholderText}>
                    {'Click on an email from the timeline to view'}
                </Typography>
            </Stack>
        );
    }

    return (<>
        <Stack className={classes.emailHeaderInfo}>
            <Typography className={classes.emailSubjectLarge}>{email.subject}</Typography>
            <Stack className={classes.emailMeta}>
                <Stack direction="row" className={classes.emailMetaRow}>
                    <Typography className={classes.emailMetaLabel}>From:</Typography>
                    <Typography className={classes.emailMetaValue}>{email.from}</Typography>
                </Stack>
                <Stack direction="row" className={classes.emailMetaRow}>
                    <Typography className={classes.emailMetaLabel}>Date:</Typography>
                    <Typography className={classes.emailMetaValue}>{moment(email.createdAt).fromNow()}</Typography>
                </Stack>
            </Stack>
        </Stack>
        <Stack className={classes.emailBlock}>
            <Typography className={classes.bodyText}>
                {bodyText}
            </Typography>
        </Stack>
    </>);
});

// Molecules
export const DateHeaderMolecule: React.FC<{ emails: EmailBody[] }> = ({ emails }) => {
    const classes = useTimelineStyles();

    const date = useMemo(() => {
        const sortedEmails = emails.sort((a, b) => moment(a.createdAt).valueOf() - moment(b.createdAt).valueOf());

        return moment(sortedEmails?.[0]?.createdAt);
    }, [emails]);

    return (
        <Stack direction="row" className={classes.dateHeader}>
            <Stack direction="row" alignItems="baseline" spacing={1}>
                <Typography className={classes.dateText}>{date.format('MMM D, YYYY')}</Typography>
                <Typography className={classes.dateRelative}> • {moment(date).fromNow()}</Typography>
            </Stack>
            <Typography className={classes.dateBadge}>
                {emails.length} email{emails.length > 1 ? 's' : ''}
            </Typography>
        </Stack>
    );
};

export const TimelineItemMolecule: React.FC<{
    email: EmailBody;
    isSelected: boolean;
    onClick: () => void;
}> = ({ email, isSelected, onClick }) => {
    const classes = useStyles();

    return (
        <Stack className={classes.timelineItem} onClick={onClick}>
            <Stack className={`${classes.timelineContent} ${isSelected ? classes.timelineContentSelected : ''}`}>
                <Stack direction="row" className={classes.emailSubject}
                    alignItems="flex-start" sx={{ marginBottom: '6px' }}>
                    <Box width="fit-content">
                        <MailIcon className={classes.emailIcon} />
                    </Box>
                    <Typography className={classes.emailSubjectText}>{email.subject}</Typography>
                </Stack>
                <Stack spacing={1} sx={{ pl: '24px', transform: 'scale(1)' }}>
                    <FolderChip label="ROOT  ＞  Folder  ＞  subfolder" />
                    <Typography className={classes.emailFrom}>From: {email.from}</Typography>
                    <Typography className={classes.emailPreview}>{email.content?.slice(0, 100)}</Typography>
                </Stack>
            </Stack>
        </Stack>
    );
};

// Organisms
export const TimelineSidebarOrganism: React.FC<{
    emails: EmailBody[];
    selectedEmail: EmailBody | null;
    onSelectEmail: (email: EmailBody) => void;
}> = ({ emails, selectedEmail, onSelectEmail }) => {
    const classes = useStyles();

    const emailsByDate = useMemo(() => {
        const grouped: Record<string, EmailBody[]> = {};
        emails.forEach(email => {
            const raw = email.createdAt ?? (email as any).time;
            const dateKey = raw ? moment(raw).format('MMM D, YYYY') : 'Unknown';
            if (!grouped[dateKey]) grouped[dateKey] = [];
            grouped[dateKey].push(email);
        });
        return grouped;
    }, [emails]);

    const sortedDates = useMemo(() => {
        const dateFMT = 'MMM D, YYYY';
        return Object.keys(emailsByDate).sort((a, b) => {
            const ma = moment(a, dateFMT, true).isValid() ? moment(a, dateFMT) : moment(a);
            const mb = moment(b, dateFMT, true).isValid() ? moment(b, dateFMT) : moment(b);
            return mb.valueOf() - ma.valueOf();
        });
    }, [emailsByDate]);

    return (
        <Stack className={classes.timelineSidebar}>
            <Stack className={classes.timelineHeader}>
                <Typography className={classes.timelineHeaderTitle}>
                    {'Email Timeline'}
                </Typography>
                <Typography className={classes.timelineCount}>
                    {emails.length} email{emails.length !== 1 ? 's' : ''}
                </Typography>
            </Stack>
            <Stack className={classes.timelineList}>
                {sortedDates.map((date, idx) => {
                    const emailsInGroup = emailsByDate[date] as EmailBody[];

                    return (
                        <Box key={idx} className={classes.dateGroup}>
                            <DateHeaderMolecule emails={emailsInGroup} />
                            {emailsInGroup.map(email => (
                                <TimelineItemMolecule
                                    key={email.id}
                                    email={email}
                                    isSelected={selectedEmail?.id === email.id}
                                    onClick={() => onSelectEmail(email)}
                                />
                            ))}
                        </Box>
                    );
                })}
            </Stack>
        </Stack>
    );
};

export const EmailViewerOrganism: React.FC<{
    selectedEmail: EmailBody | null;
}> = ({ selectedEmail }) => {
    const classes = useViewerStyles();

    return (
        <Stack className={classes.panel}>
            <Stack direction="row" className={classes.header}
                alignItems="center" justifyContent="space-between">
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    <MailIcon className={classes.emailIcon} />
                    <Typography className={classes.title}>
                        {selectedEmail?.subject || 'Select an email to view'}
                    </Typography>
                </Stack>
            </Stack>
            <Box className={classes.container}>
                <Box className={classes.frame}>
                    <EmailViewer email={selectedEmail} />
                </Box>
            </Box>
        </Stack>
    );
};

const EmailsModal: React.FC<{
    open: boolean,
    dashboard: Dashboard,
    viewableEmails: EmailBody[],
    onClose?: () => void,
}> = ({ open, dashboard, viewableEmails, onClose }) => {
    const classes = useStyles();
    const [selectedEmail, setSelectedEmail] = useState<EmailBody | null>(null);

    const handleSelectEmail = useCallback((email: EmailBody) => {
        setSelectedEmail(email);
    }, []);

    useEffect(() => {
        // if (!!viewableEmails?.length && !selectedEmail)
        setSelectedEmail(viewableEmails[0]);
    }, [viewableEmails, selectedEmail]);

    return (
        <Dialog className={classes.dialog}
            open={open} onClose={onClose} maxWidth={false}>
            <Stack direction="column" sx={{ height: '100%' }}>
                <Stack direction="row" className={classes.modalHeader}
                    alignItems="center" justifyContent="space-between">
                    <Typography className={classes.modalTitle}>
                        {dashboard.title || 'Emails'}
                    </Typography>
                    <IconButton className={classes.closeButton} onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </Stack>
                <Stack direction="row" className={classes.modalBody}
                    sx={{ flex: 1, overflow: 'hidden' }}>
                    {viewableEmails.length > 0 && (<>
                        <TimelineSidebarOrganism
                            emails={viewableEmails}
                            selectedEmail={selectedEmail}
                            onSelectEmail={handleSelectEmail} />
                        <EmailViewerOrganism selectedEmail={selectedEmail} />
                    </>)}
                </Stack>
            </Stack>
        </Dialog>
    );
};

export default EmailsModal;